import { Tooltip, Typography } from 'antd';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { getPropagationProbabilityColor } from 'utils/color/helpers';
import { EM_DASH } from 'utils/constants';

import {
  ForecastDate,
  ForecastLabel,
  ForecastRowHeader,
  PredictionTable,
  PredictionTableScroll,
  ProbabilityValue,
  TooltipLine,
} from './PredictedPropagation.style';
import {
  type PredictedEstimate,
  type PredictedForecast,
  type PredictedPropagationSummary,
  type PredictedTargetKey,
  getDisplayPercent,
  getForecastDateLabel,
  getTargetClause,
  getTargetLabel,
} from './utils';

// Severities head the columns and forecasts run down the rows. The severity levels
// are a closed set of three, so the width of the table stays bounded whatever
// number of forecasts the model returns.
type Column = { kind: 'target'; key: PredictedTargetKey } | { kind: 'growth' };

type Props = {
  summary: PredictedPropagationSummary | null;
};

export const PredictedPropagationTable = ({ summary }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!summary) {
    return (
      <Typography.Text type="secondary">
        {t('damage.predictedPropagation.none', 'No Predicted Propagation available.')}
      </Typography.Text>
    );
  }

  const { createdAt, forecasts, targetKeys } = summary;

  const columns: Column[] = targetKeys.length
    ? targetKeys.map(key => ({ kind: 'target', key }))
    : [{ kind: 'growth' }];

  const getColumnLabel = (column: Column) =>
    column.kind === 'target'
      ? getTargetLabel(column.key, t)
      : t('damage.predictedPropagation.growthRow', 'Any growth');

  const renderCell = (estimate?: PredictedEstimate | null, targetKey?: PredictedTargetKey) => {
    const display = getDisplayPercent(estimate?.value);

    if (!display) {
      return EM_DASH;
    }

    const lower = getDisplayPercent(estimate?.lowerBound)?.label;
    const upper = getDisplayPercent(estimate?.upperBound)?.label;
    const hasBounds = !isNil(lower) && !isNil(upper);

    // The column label is abbreviated, so restate the cumulative meaning in full.
    const meaning = targetKey ? getTargetClause(targetKey, display.label, t) : null;

    return (
      <Tooltip
        title={
          <>
            {meaning && <TooltipLine>{meaning}</TooltipLine>}
            <TooltipLine>
              {hasBounds
                ? t(
                    'damage.predictedPropagation.cellTooltip',
                    '{{value}}% - likely between {{lower}}% and {{upper}}%',
                    { value: display.label, lower, upper }
                  )
                : t('damage.predictedPropagation.percent', '{{value}}%', {
                    value: display.label,
                  })}
            </TooltipLine>
          </>
        }
      >
        <ProbabilityValue $color={getPropagationProbabilityColor(theme, display.percent)}>
          {t('damage.predictedPropagation.percent', '{{value}}%', { value: display.label })}
        </ProbabilityValue>
      </Tooltip>
    );
  };

  const renderColumnCell = (forecast: PredictedForecast, column: Column) =>
    column.kind === 'target'
      ? renderCell(forecast.targets[column.key], column.key)
      : renderCell(forecast.growth);

  return (
    <PredictionTableScroll>
      <PredictionTable data-testid="predicted-propagation-table">
        <thead>
          <tr>
            <th>{t('damage.predictedPropagation.forecast', 'Forecast')}</th>
            {columns.map(column => (
              <th key={column.kind === 'target' ? column.key : column.kind}>
                {getColumnLabel(column)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {forecasts.map(forecast => {
            const dateLabel = getForecastDateLabel(createdAt, forecast.years);
            return (
              <tr key={forecast.years}>
                <th scope="row">
                  <ForecastRowHeader>
                    <ForecastLabel>
                      {t('damage.predictedPropagation.horizonMonths', '{{count}} mo', {
                        count: forecast.months,
                      })}
                    </ForecastLabel>
                    {dateLabel && <ForecastDate>{dateLabel}</ForecastDate>}
                  </ForecastRowHeader>
                </th>
                {columns.map(column => (
                  <td key={column.kind === 'target' ? column.key : column.kind}>
                    {renderColumnCell(forecast, column)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </PredictionTable>
    </PredictionTableScroll>
  );
};
